/**
 * # Clean Inbox Dialog
 * 
 * Dialog for reviewing and ignoring auto-generated emails in the supplier inbox
 * Shows all emails where auto-generated detection is triggered (auto_generated_score >= 70)
 * Pre-selects all emails, user can deselect, then ignore all selected senders
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Badge } from '@/shared/components/ui/badge';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { Loader2, Ban, AlertTriangle, CheckCircle2, XCircle, Sparkles } from 'lucide-react';
import { logDev } from '@/shared/lib/log-dev';
import { format } from 'date-fns';

interface AutoGeneratedEmail {
  thread_id: string;
  from_email: string;
  from_name: string | null;
  subject: string;
  last_message_at: string;
  auto_generated_score: number;
  auto_generated_category: string | null;
  message_count: number;
}

interface CleanInboxDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onIgnored?: (ignoredCount: number) => void;
  allConversations: any[]; // EmailConversation[] from parent
  onIgnoreSenders?: (threadIds: string[]) => Promise<void>; // Optional callback to use parent's ignore function
}

export function CleanInboxDialog({
  open,
  onOpenChange,
  onIgnored,
  allConversations,
  onIgnoreSenders,
}: CleanInboxDialogProps) {
  const [loading, setLoading] = useState(false);
  const [ignoring, setIgnoring] = useState(false);
  const [autoGeneratedEmails, setAutoGeneratedEmails] = useState<AutoGeneratedEmail[]>([]);
  const [selectedThreadIds, setSelectedThreadIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filter and prepare auto-generated emails when dialog opens
  useEffect(() => {
    if (!open) {
      // Reset state when dialog closes
      setAutoGeneratedEmails([]);
      setSelectedThreadIds(new Set());
      setError(null);
      setSuccess(null);
      return;
    }

    // # Use requestIdleCallback or setTimeout to avoid blocking UI
    // This prevents the click handler from taking too long
    const processEmails = () => {
      try {
        // Filter conversations where auto_generated_score >= 70 (likely auto-generated)
        // Also deduplicate by thread_id to prevent issues
        const seenThreadIds = new Set<string>();
        const autoGenerated: AutoGeneratedEmail[] = [];

        for (const conv of allConversations) {
          const score = conv.auto_generated_score;
          if (score === null || score === undefined || score < 70) {
            continue;
          }

          const threadId = conv.thread_id?.trim();
          if (!threadId || seenThreadIds.has(threadId)) {
            continue; // Skip duplicates or invalid thread_ids
          }

          seenThreadIds.add(threadId);
          autoGenerated.push({
            thread_id: threadId,
            from_email: conv.from_email,
            from_name: conv.from_name,
            subject: conv.subject,
            last_message_at: conv.last_message_at,
            auto_generated_score: score,
            auto_generated_category: conv.auto_generated_category,
            message_count: conv.message_count,
          });
        }

        setAutoGeneratedEmails(autoGenerated);
        // Pre-select all emails
        setSelectedThreadIds(new Set(autoGenerated.map((e) => e.thread_id)));
        setError(null);
        setSuccess(null);
      } catch (error) {
        logDev.error('[CleanInboxDialog] Error processing emails', error);
        setError('Failed to process emails');
      }
    };

    // Use setTimeout to defer processing and avoid blocking click handler
    const timeoutId = setTimeout(processEmails, 0);
    return () => clearTimeout(timeoutId);
  }, [open, allConversations]);

  const handleIgnoreSelected = async () => {
    if (selectedThreadIds.size === 0) {
      setError('No emails selected');
      return;
    }

    setIgnoring(true);
    setError(null);
    setSuccess(null);

    try {
      const selectedThreadIdsArray = Array.from(selectedThreadIds);

      // If parent provides ignore function, use it (better - uses same logic as bulk ignore)
      if (onIgnoreSenders) {
        await onIgnoreSenders(selectedThreadIdsArray);
        
        // Get unique sender count for success message
        const selectedEmails = autoGeneratedEmails
          .filter((email) => selectedThreadIds.has(email.thread_id))
          .map((email) => email.from_email.toLowerCase());
        const uniqueSenderEmails = Array.from(new Set(selectedEmails));

        setSuccess(`Successfully ignored ${uniqueSenderEmails.length} sender${uniqueSenderEmails.length > 1 ? 's' : ''}`);

        // Notify parent component
        if (onIgnored) {
          onIgnored(uniqueSenderEmails.length);
        }

        // Close dialog after 2 seconds
        setTimeout(() => {
          onOpenChange(false);
        }, 2000);
      } else {
        // Fallback: Use supabase directly if parent doesn't provide function
        const { supabase } = await import('@/shared/lib/supabase');
        const { data: { user } } = await supabase.auth.getUser();

        if (!user?.id) {
          throw new Error('User not authenticated');
        }

        // Get unique sender emails from selected conversations
        const selectedEmails = autoGeneratedEmails
          .filter((email) => selectedThreadIds.has(email.thread_id))
          .map((email) => email.from_email.toLowerCase());

        const uniqueSenderEmails = Array.from(new Set(selectedEmails));

        if (uniqueSenderEmails.length === 0) {
          throw new Error('No unique senders found');
        }

        // Insert ignored senders into the database
        const ignorePromises = uniqueSenderEmails.map(async (email) => {
          const { error } = await supabase
            .from('ignored_senders')
            .upsert({
              email: email,
              ignored_by: user.id,
              ignored_at: new Date().toISOString(),
            }, {
              onConflict: 'email,ignored_by',
              ignoreDuplicates: false,
            });

          if (error) {
            logDev.error('[CleanInboxDialog] Failed to ignore sender:', { email, error });
            throw error;
          }

          return email;
        });

        const results = await Promise.allSettled(ignorePromises);
        const errors = results.filter((r) => r.status === 'rejected');

        if (errors.length > 0) {
          throw new Error(`Failed to ignore ${errors.length} sender${errors.length > 1 ? 's' : ''}`);
        }

        setSuccess(`Successfully ignored ${uniqueSenderEmails.length} sender${uniqueSenderEmails.length > 1 ? 's' : ''}`);

        // Notify parent component
        if (onIgnored) {
          onIgnored(uniqueSenderEmails.length);
        }

        // Close dialog after 2 seconds
        setTimeout(() => {
          onOpenChange(false);
        }, 2000);
      }
    } catch (err: any) {
      logDev.error('[CleanInboxDialog] Ignore error', err);
      setError(err.message || 'Failed to ignore senders');
    } finally {
      setIgnoring(false);
    }
  };

  const toggleSelection = (threadId: string) => {
    const newSelected = new Set(selectedThreadIds);
    if (newSelected.has(threadId)) {
      newSelected.delete(threadId);
    } else {
      newSelected.add(threadId);
    }
    setSelectedThreadIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedThreadIds.size === autoGeneratedEmails.length) {
      setSelectedThreadIds(new Set());
    } else {
      setSelectedThreadIds(new Set(autoGeneratedEmails.map((e) => e.thread_id)));
    }
  };

  const getCategoryColor = (category: string | null) => {
    if (!category) return 'bg-gray-500/10 text-gray-500';
    
    switch (category.toLowerCase()) {
      case 'cicd':
        return 'bg-blue-500/10 text-blue-500';
      case 'no-reply':
        return 'bg-red-500/10 text-red-500';
      case 'notification':
        return 'bg-yellow-500/10 text-yellow-500';
      case 'marketing':
        return 'bg-purple-500/10 text-purple-500';
      case 'transactional':
        return 'bg-green-500/10 text-green-500';
      default:
        return 'bg-gray-500/10 text-gray-500';
    }
  };

  const getCategoryLabel = (category: string | null) => {
    if (!category) return 'Auto-Generated';
    
    const labels: Record<string, string> = {
      'cicd': 'CI/CD',
      'no-reply': 'No-Reply',
      'notification': 'Notification',
      'marketing': 'Marketing',
      'transactional': 'Transactional',
      'generated': 'Generated',
    };
    return labels[category.toLowerCase()] || category;
  };

  // Group by sender email for better organization
  const groupedBySender = autoGeneratedEmails.reduce((acc, email) => {
    const senderEmail = email.from_email.toLowerCase();
    if (!acc[senderEmail]) {
      acc[senderEmail] = [];
    }
    acc[senderEmail].push(email);
    return acc;
  }, {} as Record<string, AutoGeneratedEmail[]>);

  const uniqueSenders = Object.keys(groupedBySender).length;
  const selectedSenders = new Set(
    Array.from(selectedThreadIds).map((threadId) => {
      const email = autoGeneratedEmails.find((e) => e.thread_id === threadId);
      return email?.from_email.toLowerCase();
    }).filter(Boolean)
  ).size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-orange-500" />
            Clean Inbox - Ignore Auto-Generated Emails
          </DialogTitle>
          <DialogDescription>
            <div className="space-y-2">
            <p>
              Review and ignore senders of emails detected as <strong>auto-generated</strong> (score ≥ 70%).
            </p>
            <p className="text-xs">
              ✅ All emails are pre-selected. Deselect any emails that are NOT auto-generated.<br />
              ⚠️ Ignoring a sender will hide all their emails from the inbox.
            </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Controls */}
          <div className="flex items-center justify-between gap-4">
            <div className="text-sm text-muted-foreground">
              {autoGeneratedEmails.length} email{autoGeneratedEmails.length !== 1 ? 's' : ''} from {uniqueSenders} sender{uniqueSenders !== 1 ? 's' : ''}
            </div>

            {autoGeneratedEmails.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={toggleSelectAll}
                disabled={ignoring}
              >
                {selectedThreadIds.size === autoGeneratedEmails.length ? 'Deselect All' : 'Select All'}
              </Button>
            )}
          </div>

          {/* Status Messages */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-md">
              <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-500">{error}</p>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-md">
              <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
              <p className="text-sm text-green-500">{success}</p>
            </div>
          )}

          {/* Email List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : autoGeneratedEmails.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <CheckCircle2 className="h-12 w-12 text-green-500 mb-3" />
              <p className="text-lg font-medium">No auto-generated emails found</p>
              <p className="text-sm text-muted-foreground mt-1">
                Your inbox is clean! All emails appear to be from real people.
              </p>
            </div>
          ) : (
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-4">
                {Object.entries(groupedBySender).map(([senderEmail, emails]) => {
                  const allSelected = emails.every((e) => selectedThreadIds.has(e.thread_id));
                  const someSelected = emails.some((e) => selectedThreadIds.has(e.thread_id));

                  return (
                    <div key={senderEmail} className="border rounded-lg p-3 space-y-2">
                      {/* Sender Header */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={allSelected}
                            onCheckedChange={() => {
                              if (allSelected) {
                                // Deselect all from this sender
                                const newSelected = new Set(selectedThreadIds);
                                emails.forEach((e) => newSelected.delete(e.thread_id));
                                setSelectedThreadIds(newSelected);
                              } else {
                                // Select all from this sender
                                const newSelected = new Set(selectedThreadIds);
                                emails.forEach((e) => newSelected.add(e.thread_id));
                                setSelectedThreadIds(newSelected);
                              }
                            }}
                            disabled={ignoring}
                            className="mt-1"
                          />
                          <div>
                            <p className="font-medium text-sm">
                              {emails[0].from_name || emails[0].from_email}
                            </p>
                            <p className="text-xs text-muted-foreground">{senderEmail}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {emails.length} email{emails.length !== 1 ? 's' : ''}
                        </Badge>
                      </div>

                      {/* Emails from this sender */}
                      <div className="space-y-1 pl-8">
                        {emails.map((email) => (
                          <div
                            key={email.thread_id}
                            className="flex items-start gap-3 p-2 border rounded hover:bg-accent/50 transition-colors"
                          >
                            <Checkbox
                              checked={selectedThreadIds.has(email.thread_id)}
                              onCheckedChange={() => toggleSelection(email.thread_id)}
                              disabled={ignoring}
                              className="mt-1"
                            />

                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">
                                    {email.subject || 'No subject'}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {format(new Date(email.last_message_at), 'MMM d, yyyy HH:mm')}
                                    {email.message_count > 1 && ` • ${email.message_count} messages`}
                                  </p>
                                </div>

                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <Badge className={getCategoryColor(email.auto_generated_category)}>
                                    {getCategoryLabel(email.auto_generated_category)}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs">
                                    {Math.round(email.auto_generated_score)}%
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </div>

        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-muted-foreground">
              {selectedThreadIds.size} email{selectedThreadIds.size !== 1 ? 's' : ''} selected from {selectedSenders} sender{selectedSenders !== 1 ? 's' : ''}
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={ignoring}
              >
                Cancel
              </Button>

              <Button
                variant="default"
                onClick={handleIgnoreSelected}
                disabled={selectedThreadIds.size === 0 || ignoring}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                {ignoring ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Ignoring...
                  </>
                ) : (
                  <>
                    <Ban className="h-4 w-4 mr-2" />
                    Ignore {selectedSenders} Sender{selectedSenders !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

